<template>
  <v-card outlined class="pa-6">
    <Typography as="h4" variant="h-4" class="text-left mb-6">Impact Summary</Typography>

    <template v-if="loading">
      <v-skeleton-loader type="image" height="88" class="rounded-lg mb-4" />
      <v-skeleton-loader type="image" height="88" class="rounded-lg mb-4" />
      <v-skeleton-loader type="image" height="88" class="rounded-lg" />
    </template>

    <template v-else-if="impact">
      <ImpactStatCard
        icon="mdi-wallet"
        color="#379f0a"
        bg-color="#f0fdf4"
        label="Wallet"
        :rows="walletRows"
        class="mb-4"
      />

      <ImpactStatCard
        v-if="impact.removeUser.recognitions || impact.keepUser.recognitions"
        icon="mdi-party-popper"
        color="#003cff"
        bg-color="#eef2ff"
        label="Recognitions"
        :rows="recognitionRows"
        class="mb-4"
      />

      <ImpactStatCard
        v-if="impact.removeUser.programs || impact.keepUser.programs"
        icon="mdi-star"
        color="#8000ff"
        bg-color="#faf5ff"
        label="Programs"
        :rows="programRows"
      />

      <v-divider class="my-6" />

      <Typography variant="body-2" class="text-left"
        >Card will be cancelled (if applicable)</Typography
      >
    </template>
  </v-card>
</template>

<script>
import ImpactStatCard from "@/components/merge-users/ImpactStatCard.vue";
import Typography from "@/components/generic/Typography.vue";
import formatCurrency from "@/mixins/formatCurrency.js";

export default {
  name: "MergeImpactSummary",
  components: { ImpactStatCard, Typography },
  mixins: [formatCurrency],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    // Shape: { keepUser, removeUser } each with { cashBalance, otherBalances: [{ displayName, balance, unitPlural, unitSingular }], recognitions, programs }
    impact: {
      type: Object,
      default: null,
    },
  },
  computed: {
    walletRows() {
      const { removeUser, keepUser } = this.impact;
      const rows = [
        {
          label: "Cash",
          remove: this.formatCurrency(removeUser.cashBalance),
          keep: this.formatCurrency(keepUser.cashBalance),
          combined: this.formatCurrency(removeUser.cashBalance + keepUser.cashBalance),
        },
      ];
      const keepMap = {};
      (keepUser.otherBalances || []).forEach((b) => {
        keepMap[b.awardId] = b;
      });

      const seen = new Set();
      (removeUser.otherBalances || []).forEach((rb) => {
        seen.add(rb.awardId);
        const kb = keepMap[rb.awardId] || { balance: 0 };
        rows.push({
          label: rb.displayName,
          remove: this.formatCurrency(rb.balance, rb),
          keep: this.formatCurrency(kb.balance, rb),
          combined: this.formatCurrency(rb.balance + kb.balance, rb),
        });
      });

      // include awards the keep user has that the remove user doesn't
      (keepUser.otherBalances || [])
        .filter((b) => !seen.has(b.awardId))
        .forEach((kb) => {
          rows.push({
            label: kb.displayName,
            remove: this.formatCurrency(0, kb),
            keep: this.formatCurrency(kb.balance, kb),
            combined: this.formatCurrency(kb.balance, kb),
          });
        });
      return rows;
    },
    recognitionRows() {
      const { removeUser, keepUser } = this.impact;
      return [
        {
          remove: removeUser.recognitions,
          keep: keepUser.recognitions,
          combined: removeUser.recognitions + keepUser.recognitions,
        },
      ];
    },
    programRows() {
      const { removeUser, keepUser } = this.impact;
      return [
        {
          remove: removeUser.programs,
          keep: keepUser.programs,
          combined: removeUser.programs + keepUser.programs,
        },
      ];
    },
  },
};
</script>
